import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { useCountdown, useIsClient } from 'usehooks-ts';
import { GenericAnnouncementBarContent } from '@/types/amplience';
import { initialPromoCountAtom, isCurrentPromoActivatedAtom } from '@/atoms/promoAtom';
import { getPromoCountdown } from '@/utility/getPromoCountdown';
import AnnouncementBar, { textColorMap } from '../AnnouncementBar/AnnouncementBar';
import DynamicIcon from '../Common/DynamicIcon';
import { getLocalizedValue } from '../PromoPopup/PromoPopup';
const isWithin48Hours = (expiryDate: string) => {
  if (!expiryDate) return false;
  const now = Date.now();
  const endTime = new Date(expiryDate).getTime();
  const diff = endTime - now;
  return diff > 0 && diff <= 48 * 60 * 60 * 1000;
};
const CountdownTimer = t0 => {
  const $ = _c(21);
  const {
    endDate,
    textColor
  } = t0;
  const t = useTranslations("promo");
  const isClient = useIsClient();
  const initialCount = useAtomValue(initialPromoCountAtom);
  const [countStart, setCountStart] = useState(initialCount);
  let t1;
  if ($[0] !== countStart) {
    t1 = {
      countStart
    };
    $[0] = countStart;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [count, t2] = useCountdown(t1);
  const {
    startCountdown,
    resetCountdown
  } = t2;
  let t3;
  if ($[2] !== endDate || $[3] !== isClient || $[4] !== resetCountdown || $[5] !== startCountdown) {
    t3 = () => {
      if (!isClient) {
        return;
      }
      setCountStart(getPromoCountdown(endDate));
      resetCountdown();
      startCountdown();
    };
    $[2] = endDate;
    $[3] = isClient;
    $[4] = resetCountdown;
    $[5] = startCountdown;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== endDate || $[8] !== isClient) {
    t4 = [isClient, endDate];
    $[7] = endDate;
    $[8] = isClient;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  const bgClass = textColor === "white" ? "bg-white text-black" : "bg-black text-white";
  let t5;
  if ($[10] !== bgClass || $[11] !== count || $[12] !== t) {
    let t6;
    let t7;
    let t8;
    if ($[14] !== bgClass || $[15] !== count) {
      t6 = () => <span className={clsx("inline-block w-6 rounded-xs text-center", bgClass)}>{String(Math.floor(count / 3600)).padStart(2, "0")}</span>;
      t7 = () => <span className={clsx("inline-block w-6 rounded-xs text-center", bgClass)}>{String(Math.floor(count / 60 % 60)).padStart(2, "0")}</span>;
      t8 = () => <span className={clsx("inline-block w-6 rounded-xs text-center", bgClass)}>{String(Math.floor(count % 60)).padStart(2, "0")}</span>;
      $[14] = bgClass;
      $[15] = count;
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
    } else {
      t6 = $[16];
      t7 = $[17];
      t8 = $[18];
    }
    t5 = t.rich("countdown", {
      hours: t6,
      minutes: t7,
      seconds: t8
    });
    $[10] = bgClass;
    $[11] = count;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[19] !== t5) {
    t6 = <span className="ml-2 flex items-center gap-0.5">{t5}</span>;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
type PromoAnnouncementBarProps = {
  content: GenericAnnouncementBarContent;
  expiryDate?: string;
  onCtaClick?: () => void;
};
export const PromoAnnouncementBar = props => {
  const $ = _c(43);
  const t = useTranslations();
  const isPromoActivated = useAtomValue(isCurrentPromoActivatedAtom);
  const isClient = useIsClient();
  const isActivated = isClient && isPromoActivated;
  const {
    expiryDate,
    content
  } = props;
  const {
    showCountdown,
    textColor,
    backgroundColor
  } = content;
  const locale = useLocale();
  let t0;
  if ($[0] !== content.title || $[1] !== locale) {
    t0 = getLocalizedValue(content.title, locale);
    $[0] = content.title;
    $[1] = locale;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const title = t0;
  let t1;
  if ($[3] !== content.ctaLabel || $[4] !== locale) {
    t1 = content.ctaLabel ? getLocalizedValue(content.ctaLabel, locale) : "";
    $[3] = content.ctaLabel;
    $[4] = locale;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const ctaLabel = t1;
  let t2;
  if ($[6] !== content.ctaLabel || $[7] !== isActivated || $[8] !== locale || $[9] !== t) {
    t2 = isActivated ? t("promo.codeActivated") : content.ctaLabel ? getLocalizedValue(content.ctaLabel, locale) : "";
    $[6] = content.ctaLabel;
    $[7] = isActivated;
    $[8] = locale;
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const label = t2;
  const t3 = ctaLabel || title;
  const t4 = props.onCtaClick;
  let t5;
  if ($[11] !== title) {
    t5 = <span className="block font-medium sm:inline">{title}</span>;
    $[11] = title;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== expiryDate || $[14] !== showCountdown || $[15] !== textColor) {
    t6 = showCountdown && expiryDate && isWithin48Hours(expiryDate) && <div className="hidden md:block"><CountdownTimer endDate={expiryDate} textColor={textColor} /></div>;
    $[13] = expiryDate;
    $[14] = showCountdown;
    $[15] = textColor;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = isActivated ? "opacity-0" : "opacity-100";
  let t8;
  if ($[17] !== t7) {
    t8 = clsx("absolute inset-0 transition-opacity duration-150", t7);
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DynamicIcon icon="copy" className="size-4" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8) {
    t10 = <span className={t8}>{t9}</span>;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isActivated ? "opacity-100" : "opacity-0";
  let t12;
  if ($[22] !== t11) {
    t12 = clsx("absolute inset-0 transition-opacity duration-150", t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DynamicIcon icon="checkcircle" className="size-4" />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12) {
    t14 = <span className={t12}>{t13}</span>;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t10 || $[28] !== t14) {
    t15 = <span className="relative inline-block size-4 align-middle" aria-hidden="true">{t10}{t14}</span>;
    $[27] = t10;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== ctaLabel || $[31] !== label || $[32] !== textColor) {
    t16 = ctaLabel && <span className={clsx("font-medium underline transition-opacity hover:opacity-80 sm:mt-0 sm:ml-2", textColorMap[textColor])} aria-hidden="true">{label}</span>;
    $[30] = ctaLabel;
    $[31] = label;
    $[32] = textColor;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== backgroundColor || $[35] !== props.onCtaClick || $[36] !== t15 || $[37] !== t16 || $[38] !== t3 || $[39] !== t5 || $[40] !== t6 || $[41] !== textColor) {
    t17 = <AnnouncementBar ariaLabel={t3} backgroundColor={backgroundColor} textColor={textColor} onCtaClick={t4}>{t5}{t6}{t15}{t16}</AnnouncementBar>;
    $[34] = backgroundColor;
    $[35] = props.onCtaClick;
    $[36] = t15;
    $[37] = t16;
    $[38] = t3;
    $[39] = t5;
    $[40] = t6;
    $[41] = textColor;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
};